import { Box, Stack } from '@chakra-ui/react'
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaSearch } from 'react-icons/fa'

import { Button } from '@/components/ui/button'
import { InputGroup } from '@/components/ui/input-group'
import { env } from '@/config/env'

import { FullScreenLoader } from '../../Loader/FullScreenLoader'
import { useMap } from '../MapContext'
import { useGeocoding, useMarker } from './hooks'
import { SearchInput } from './SearchInput'
import { AddressSearchProps } from './types'
import { containerStyle, extractAddressInfo } from './utils'

export const AddressSearch: React.FC<AddressSearchProps> = ({
  onAddressChanged,
  defaultValue,
  placeholder,
  mode = 'map',
  width = '100%',
}) => {
  const { t } = useTranslation()
  const { map, setMap, currentCenter, updateMapCenter } = useMap()
  const { initializeGeocoder, geocodeAddress, reverseGeocode } = useGeocoding()
  const { marker, updateMarker, clearMarker } = useMarker(map)
  const [searchValue, setSearchValue] = useState('')
  const isProgrammaticUpdateRef = useRef(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null)

  const { isLoaded } = useJsApiLoader({
    id: 'google-map-script',
    googleMapsApiKey: env.GOOGLE_MAPS_API_KEY || '',
    libraries: ['places'],
  })

  const handlePlaceSelected = useCallback(
    async (
      place: google.maps.places.PlaceResult,
      shouldUpdateView: boolean = true
    ) => {
      if (place.geometry && place.geometry.location) {
        const newPosition = {
          lat: place.geometry.location.lat(),
          lng: place.geometry.location.lng(),
        }

        if (shouldUpdateView && mode === 'map') {
          updateMapCenter(newPosition)
        }

        if (mode === 'map') {
          updateMarker(newPosition)
        }

        const addressInfo = extractAddressInfo(place.address_components || [])
        const newAddress = place.formatted_address || ''

        isProgrammaticUpdateRef.current = true
        setSearchValue(newAddress)
        if (searchInputRef.current) {
          searchInputRef.current.value = newAddress
        }
        isProgrammaticUpdateRef.current = false

        onAddressChanged({
          coordinates: newPosition,
          address: newAddress,
          ...addressInfo,
        })
      }
    },
    [updateMapCenter, updateMarker, onAddressChanged, mode]
  )

  useEffect(() => {
    if (isLoaded && searchInputRef.current && mode === 'light') {
      autocompleteRef.current = new google.maps.places.Autocomplete(
        searchInputRef.current,
        {
          types: ['address'],
          fields: ['address_components', 'geometry', 'formatted_address'],
        }
      )

      autocompleteRef.current.addListener('place_changed', () => {
        const place = autocompleteRef.current?.getPlace()
        if (place) {
          handlePlaceSelected(place, false)
        }
      })
    }

    return () => {
      if (autocompleteRef.current) {
        google.maps.event.clearInstanceListeners(autocompleteRef.current)
      }
    }
  }, [isLoaded, mode, handlePlaceSelected])

  // Run address search in map mode
  const handleSearchClick = async () => {
    if (searchValue) {
      const result = await geocodeAddress(searchValue)
      if (result) {
        handlePlaceSelected(result, true)
      }
    }
  }

  const onLoad = useCallback(
    (mapInstance: google.maps.Map) => {
      setMap(mapInstance)
      initializeGeocoder()

      if (mode === 'map') {
        const initialPosition = {
          lat: defaultValue?.latitude
            ? Number.parseFloat(defaultValue.latitude)
            : 48.856_614,
          lng: defaultValue?.longitude
            ? Number.parseFloat(defaultValue.longitude)
            : 2.352_221_9,
        }
        updateMarker(initialPosition, true)
        updateMapCenter(initialPosition)

        mapInstance.addListener(
          'dblclick',
          async (e: google.maps.MapMouseEvent) => {
            const { latLng } = e
            if (!latLng) return

            updateMarker(latLng.toJSON())

            const result = await reverseGeocode(latLng)
            if (result) {
              const newAddress = result.formatted_address
              isProgrammaticUpdateRef.current = true
              setSearchValue(newAddress)
              if (searchInputRef.current) {
                searchInputRef.current.value = newAddress
              }
              isProgrammaticUpdateRef.current = false

              const addressInfo = extractAddressInfo(
                result.address_components || []
              )

              onAddressChanged({
                coordinates: latLng.toJSON(),
                address: newAddress,
                ...addressInfo,
              })
            }
          }
        )
      }
    },
    [
      defaultValue?.latitude,
      defaultValue?.longitude,
      setMap,
      initializeGeocoder,
      updateMarker,
      updateMapCenter,
      reverseGeocode,
      onAddressChanged,
      mode,
    ]
  )

  const onUnmount = useCallback(() => {
    setMap(null)
    clearMarker()
  }, [setMap, clearMarker])

  useEffect(() => {
    const nextAddress = defaultValue?.address || ''
    setSearchValue(nextAddress)
    if (searchInputRef.current) {
      searchInputRef.current.value = nextAddress
    }
  }, [defaultValue?.address])

  if (!isLoaded) {
    return <FullScreenLoader />
  }

  return (
    <Stack style={{ width: '100%' }} gap={2}>
      {mode === 'map' ? (
        <InputGroup
          width={width}
          endElement={
            <Button
              h="1.75rem"
              size="sm"
              onClick={handleSearchClick}
              aria-label={t('global:search')}
            >
              <FaSearch />
            </Button>
          }
        >
          <SearchInput
            ref={searchInputRef}
            value={searchValue}
            onChange={(value) => {
              isProgrammaticUpdateRef.current = false
              setSearchValue(value)
            }}
            placeholder={placeholder || t('global:typeAnAddress.placeholder')}
            width={width}
          />
        </InputGroup>
      ) : (
        <SearchInput
          ref={searchInputRef}
          value={searchValue}
          onChange={(value) => {
            isProgrammaticUpdateRef.current = false
            setSearchValue(value)
          }}
          placeholder={placeholder || t('global:typeAnAddress.placeholder')}
          width={width}
        />
      )}
      {mode === 'map' && (
        <Box rounded={'xl'}>
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={currentCenter}
            zoom={15}
            onLoad={onLoad}
            onUnmount={onUnmount}
            options={{
              gestureHandling: 'cooperative',
              disableDoubleClickZoom: true,
            }}
          >
            {marker && (
              <Marker
                position={marker.getPosition() as google.maps.LatLng}
                draggable={true}
                onDragEnd={async (e) => {
                  if (e.latLng) {
                    const result = await reverseGeocode(e.latLng)
                    if (result) {
                      const addressInfo = extractAddressInfo(
                        result.address_components || []
                      )
                      onAddressChanged({
                        coordinates: {
                          lat: e.latLng.lat(),
                          lng: e.latLng.lng(),
                        },
                        address: result.formatted_address,
                        ...addressInfo,
                      })
                    }
                  }
                }}
              />
            )}
          </GoogleMap>
        </Box>
      )}
    </Stack>
  )
}
